import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { defer, LoaderFunctionArgs, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import { InfiniteData, QueryClient } from '@tanstack/react-query';
import { TFunction } from 'i18next';

import { createHandle } from 'services/breadcrumbs/breadcrumbs';
import { ConnectionsResponseDTO, useInfiniteConnections } from 'services/repositories/connections/connections';
import { useCurrentPartner, usePartner } from 'services/repositories/partners/partners';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { formatDayAndTime } from 'support/helpers/dateTime/dateTime';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { RouteParams } from 'support/helpers/types/types';
import { ConnectionsRoutes, NetworkRoutes, PartnerDTO, RelationshipV2 } from 'support/types';
import {
  ConnectionNavigationProvider,
  ConnectionOrigin,
} from 'pages/Connections/ConnectionDetailsPage/contexts/ConnectionNavigationContext';
import { ConnectionsFilterKey } from 'pages/Connections/tableConfig';
import { NewTradeRequest } from 'pages/TradePartners/NewTradeRequest/NewTradeRequest';
import { ConnectionsActionRequiredBadge } from 'components/ConnectionsActionRequiredBadge/ConnectionsActionRequiredBadge';
import { Button } from 'components/Form/Button/Button';
import { ListTable } from 'components/ListTable/ListTable';
import { Page } from 'components/Page/Page';

import { PartnerV2WithRelationships, usePartnerToConnectTo } from './hooks/usePartnerToConnectTo';
import { RelationshipBadge } from '../components/RelationshipBadge/RelationshipBadge';
import { NoConnection } from './components/NoConnection/NoConnection';
import { ResendInvitationEmailAlert } from './components/ResendInvitationEmailAlert/ResendInvitationEmailAlert';

type ConnectionDetailsPageParams = RouteParams<typeof ConnectionsRoutes.connectionDetails>;
type NetworkDetailsPageParams = RouteParams<typeof NetworkRoutes.connectionDetails>;
type ConnectionDetailsPageProps = {
  origin: ConnectionOrigin;
};

type PartnerForTradeRequest = Pick<PartnerDTO, 'id' | 'documentTypes' | 'logoUrl' | 'name'>;

export type ConnectionDetailsLocationState = {
  partner?: PartnerForTradeRequest;
  partnerWithRelationships?: PartnerV2WithRelationships;
  hasFilteredRelationships?: boolean;
};

export const ConnectionDetailsPage = ({ origin }: ConnectionDetailsPageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { partnerId } = useParams<ConnectionDetailsPageParams>();
  const [openNewTradeRequest, setOpenNewTradeRequest] = useState(false);
  const { data: currentPartner } = useCurrentPartner();

  const locationState = location.state as ConnectionDetailsLocationState | undefined;

  const {
    partner,
    partnerWithRelationships,
    relationships,
    isLoading,
    isLoadingFullRelationships,
    showNoConnection,
    isWaitingForPartnerOnboarding,
  } = usePartnerToConnectTo(partnerId, {
    partnerForTradeRequest: locationState?.partner,
    partnerWithRelationships: locationState?.partnerWithRelationships,
    hasFilteredRelationships: locationState?.hasFilteredRelationships,
  });

  const tableHeaders = useMemo(
    () => [
      { key: 'documentType', label: t('connections:partnerConnectionDetails.table.header.documentType') },
      { key: 'status', label: t('connections:partnerConnectionDetails.table.header.status') },
      { key: 'lastUpdated', label: t('connections:partnerConnectionDetails.table.header.lastUpdated') },
      { key: 'actionRequired', label: '' },
    ],
    [t],
  );

  const buildRow = useCallback(
    (relationship: RelationshipV2) => {
      return [
        {
          key: `documentType-${relationship.id}`,
          element: (
            <span className="block max-w-xs truncate">
              {t(`common:messageTypes.plural.${relationship.message_type}`)}
            </span>
          ),
        },
        {
          key: `status-${relationship.id}`,
          element: <RelationshipBadge relationship={relationship} currentPartnerId={currentPartner?.id || ''} />,
        },
        {
          key: `lastUpdated-${relationship.id}`,
          element: relationship.updated_at ? (
            <span className="tabular-nums">
              {formatDayAndTime(
                relationship.timeline_events?.length
                  ? relationship.timeline_events[0].created_at
                  : relationship.updated_at,
              )}
            </span>
          ) : (
            EMPTY_FIELD
          ),
        },
        {
          key: `actionRequired-${relationship.id}`,
          element: (
            <ConnectionsActionRequiredBadge
              relationships={[relationship]}
              currentPartnerId={currentPartner?.id || ''}
            />
          ),
        },
      ];
    },
    [t, currentPartner?.id],
  );

  if (!partnerId) {
    return null;
  }

  if (showNoConnection && partner) {
    return (
      <ConnectionNavigationProvider origin={origin}>
        <Page isLoading={isLoading}>
          <Page.Head />
          <Page.Section>
            <NoConnection onRequestConnection={() => setOpenNewTradeRequest(true)} />
          </Page.Section>

          <NewTradeRequest
            showPopup={openNewTradeRequest}
            onClose={() => setOpenNewTradeRequest(false)}
            partner={partner}
            connected={false}
          />
        </Page>
      </ConnectionNavigationProvider>
    );
  }

  return (
    <ConnectionNavigationProvider origin={origin}>
      <Page isLoading={isLoading}>
        <Page.Head
          rightContent={
            <Button
              onClick={() => setOpenNewTradeRequest(true)}
              LeftIcon={PlusIcon}
              loading={isLoading}
              analyticsId="connections:new_trade_request"
            >
              {t('connections:partnerConnectionDetails.newTradeRequest')}
            </Button>
          }
        />
        <Page.Section>
          {partnerWithRelationships && isWaitingForPartnerOnboarding && (
            <div className="mb-4">
              <ResendInvitationEmailAlert partner={partnerWithRelationships} />
            </div>
          )}

          <ListTable<RelationshipV2>
            headers={tableHeaders}
            data={relationships}
            isLoading={isLoading}
            isLoadingMore={isLoadingFullRelationships}
            loadingRowsCount={3}
            rowBuilder={buildRow}
            onRowClick={(relationship) =>
              navigate(
                routeToPage(
                  origin === 'network' ? NetworkRoutes.relationshipDetails : ConnectionsRoutes.relationshipDetails,
                  {
                    partnerId: partnerId,
                    relationshipId: relationship.id,
                  },
                ),
              )
            }
          />
        </Page.Section>

        {partner && (
          <NewTradeRequest
            showPopup={openNewTradeRequest}
            onClose={() => setOpenNewTradeRequest(false)}
            partner={partner}
            connected
          />
        )}
      </Page>
    </ConnectionNavigationProvider>
  );
};

type ConnectionDetailsPageData = InfiniteData<ConnectionsResponseDTO>;

ConnectionDetailsPage.loader = (queryClient: QueryClient) => async (args: LoaderFunctionArgs) => {
  const partnerId = args.params.partnerId;
  if (!partnerId) {
    return defer({ data: Promise.resolve(undefined) });
  }

  const queryKey = useInfiniteConnections.getKey({
    query: {
      filterValues: [{ key: ConnectionsFilterKey.partnerId, value: partnerId }],
      perPage: null,
    },
  });

  const cachedData = queryClient.getQueryData<ConnectionDetailsPageData>(queryKey);

  // special handling for paginated responses
  return defer({
    data: cachedData
      ? Promise.resolve(cachedData)
      : queryClient.fetchInfiniteQuery(queryKey, useInfiniteConnections.queryFn, {
          getNextPageParam: (lastPage: ConnectionsResponseDTO) => lastPage.meta.next_cursor,
        }),
  });
};

ConnectionDetailsPage.handle = createHandle<ConnectionDetailsPageData, ConnectionDetailsPageParams>((params, data) => {
  return {
    label: (_t: TFunction) => {
      const partner = data?.pages?.flatMap((page) => page.data).find((p) => p.id === params.partnerId);
      return partner?.name ?? '...';
    },
    to: routeToPage(ConnectionsRoutes.connectionDetails, { partnerId: params.partnerId }),
    key: `connection-details-${params.partnerId}`,
  };
});

type NetworkPartnerPageData = PartnerDTO;

ConnectionDetailsPage.loaderFromNetwork = (queryClient: QueryClient) => async (args: LoaderFunctionArgs) => {
  const partnerId = args.params.partnerId;
  if (!partnerId) {
    return defer({ data: Promise.resolve(undefined) });
  }

  const queryKey = usePartner.getKey({ id: partnerId });
  const cachedData = queryClient.getQueryData<NetworkPartnerPageData>(queryKey);

  return defer({
    data: cachedData ? Promise.resolve(cachedData) : queryClient.fetchQuery(queryKey, usePartner.queryFn),
  });
};

ConnectionDetailsPage.handleFromNetwork = createHandle<NetworkPartnerPageData, NetworkDetailsPageParams>(
  (params, data) => {
    return {
      label: (_t: TFunction) => data?.name ?? '...',
      to: routeToPage(NetworkRoutes.connectionDetails, { partnerId: params.partnerId }),
      key: `network-partner-${params.partnerId}`,
    };
  },
);
